// QA scope-area catalog admin (Loans.ManageQaCatalog). Lists the areas
// (active + inactive), supports reorder, create, edit (name / purpose /
// owner role / on-by-default / active) and an ordered checklist editor. Code
// and ItemCode are server-generated and immutable; edits propagate to every
// loan's QA review (areas are referenced by Code). Reached from the QA tab
// header.

import { useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Badge, Button, Skeleton } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateQaScopeArea,
  useQaScopeAreas,
  useReorderQaScopeAreas,
  useUpdateQaScopeArea,
} from "@/hooks/portfolioAdmin/useQaReviews";
import type {
  LoanQaScopeAreaCatalogItemDto,
  UpsertLoanQaChecklistItemRequest,
} from "@/api/portfolioAdmin/qaReviewsApi";

const inputClass =
  "w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

interface ItemDraft {
  id?: string;
  label: string;
  helpText: string;
  required: boolean;
}

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; area: LoanQaScopeAreaCatalogItemDto }
  | null;

export function QaScopeAreaCatalogModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const areasQuery = useQaScopeAreas(true, open);
  const reorder = useReorderQaScopeAreas();
  const [editor, setEditor] = useState<EditorState>(null);

  const areas = areasQuery.data ?? [];

  const move = async (index: number, dir: -1 | 1) => {
    const next = [...areas];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await reorder.mutateAsync(next.map((a) => a.id));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="QA scope areas"
      description="Areas and checklists apply to every loan's QA review. Codes never change."
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {editor ? (
        <AreaEditor
          state={editor}
          onDone={() => setEditor(null)}
        />
      ) : (
        <div className="flex flex-col gap-space-3">
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setEditor({ mode: "create" })}
              data-testid="qa-catalog-add"
            >
              + Add area
            </Button>
          </div>
          {areasQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : areas.length === 0 ? (
            <p className="text-small text-gray-mid m-0">No scope areas yet.</p>
          ) : (
            <ul className="m-0 list-none p-0 divide-y divide-border-light">
              {areas.map((area, idx) => (
                <li
                  key={area.id}
                  className="flex items-center gap-space-3 py-space-2"
                  data-testid={`qa-catalog-row-${area.code}`}
                >
                  <div className="flex flex-col">
                    <button
                      type="button"
                      className="p-[2px] text-gray-mid hover:text-onyx disabled:opacity-40"
                      disabled={idx === 0 || reorder.isPending}
                      onClick={() => void move(idx, -1)}
                      aria-label={`Move ${area.name} up`}
                    >
                      <ArrowUp size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="p-[2px] text-gray-mid hover:text-onyx disabled:opacity-40"
                      disabled={idx === areas.length - 1 || reorder.isPending}
                      onClick={() => void move(idx, 1)}
                      aria-label={`Move ${area.name} down`}
                    >
                      <ArrowDown size={14} aria-hidden />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-space-2">
                      <span className="text-small font-semibold text-onyx">
                        {area.name}
                      </span>
                      <span className="text-caption text-gray-mid">{area.code}</span>
                      <span className="text-caption text-gray-mid">
                        Owner {area.ownerRoleCode}
                      </span>
                      {area.onByDefault && <Badge status="gold">On by default</Badge>}
                      {!area.active && <Badge status="archived">Inactive</Badge>}
                    </div>
                    <p className="text-caption text-gray-mid m-0 mt-[2px]">
                      {area.checklistItems.length} checklist item
                      {area.checklistItems.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setEditor({ mode: "edit", area })}
                    data-testid={`qa-catalog-edit-${area.code}`}
                  >
                    Edit
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

function AreaEditor({
  state,
  onDone,
}: {
  state: Exclude<EditorState, null>;
  onDone: () => void;
}) {
  const toast = useToast();
  const create = useCreateQaScopeArea();
  const update = useUpdateQaScopeArea();
  const editing = state.mode === "edit" ? state.area : null;

  const [name, setName] = useState(editing?.name ?? "");
  const [purpose, setPurpose] = useState(editing?.purpose ?? "");
  const [ownerRoleCode, setOwnerRoleCode] = useState(editing?.ownerRoleCode ?? "B1");
  const [onByDefault, setOnByDefault] = useState(editing?.onByDefault ?? true);
  const [active, setActive] = useState(editing?.active ?? true);
  const [items, setItems] = useState<ItemDraft[]>(
    () =>
      editing?.checklistItems
        .filter((i) => i.active)
        .map((i) => ({
          id: i.id,
          label: i.label,
          helpText: i.helpText ?? "",
          required: i.required,
        })) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const setItem = (idx: number, patch: Partial<ItemDraft>) =>
    setItems((all) => all.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const save = async () => {
    setError(null);
    const checklistItems: UpsertLoanQaChecklistItemRequest[] = items
      .filter((i) => i.label.trim() !== "")
      .map((i) => ({
        id: i.id ?? null,
        label: i.label.trim(),
        helpText: i.helpText.trim() === "" ? null : i.helpText.trim(),
        required: i.required,
      }));
    try {
      if (state.mode === "create") {
        await create.mutateAsync({
          name: name.trim(),
          purpose: purpose.trim() === "" ? null : purpose.trim(),
          ownerRoleCode: ownerRoleCode.trim(),
          onByDefault,
          checklistItems,
        });
        toast.success("Scope area created");
      } else {
        await update.mutateAsync({
          id: state.area.id,
          body: {
            name: name.trim(),
            purpose: purpose.trim() === "" ? null : purpose.trim(),
            ownerRoleCode: ownerRoleCode.trim(),
            onByDefault,
            active,
            checklistItems,
          },
        });
        toast.success("Scope area saved");
      }
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <div className="flex flex-col gap-space-3" data-testid="qa-catalog-editor">
      <div>
        <label className="text-caption font-semibold text-gray-mid">Name</label>
        <input
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="qa-catalog-name"
        />
        {editing && (
          <p className="text-caption text-gray-mid m-0 mt-[2px]">
            Code {editing.code} (immutable)
          </p>
        )}
      </div>
      <div>
        <label className="text-caption font-semibold text-gray-mid">Purpose</label>
        <textarea
          className={`${inputClass} min-h-[64px]`}
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          data-testid="qa-catalog-purpose"
        />
      </div>
      <div className="flex flex-wrap items-center gap-space-4">
        <div>
          <label className="text-caption font-semibold text-gray-mid">
            Owner role
          </label>
          <input
            className={`${inputClass} w-28`}
            value={ownerRoleCode}
            onChange={(e) => setOwnerRoleCode(e.target.value)}
            data-testid="qa-catalog-owner"
          />
        </div>
        <label className="flex items-center gap-space-2 text-small">
          <input
            type="checkbox"
            checked={onByDefault}
            onChange={(e) => setOnByDefault(e.target.checked)}
            data-testid="qa-catalog-onbydefault"
          />
          On by default
        </label>
        {editing && (
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="qa-catalog-active"
            />
            Active
          </label>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-space-1">
          <label className="text-caption font-semibold text-gray-mid">
            Checklist items
          </label>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() =>
              setItems((all) => [...all, { label: "", helpText: "", required: false }])
            }
            data-testid="qa-catalog-add-item"
          >
            + Add item
          </Button>
        </div>
        <div className="flex flex-col gap-space-2">
          {items.map((item, idx) => (
            <div
              key={item.id ?? `new-${idx}`}
              className="rounded-md border border-border-light p-space-2 flex flex-col gap-space-1"
            >
              <input
                aria-label={`Checklist item ${idx + 1} label`}
                className={inputClass}
                placeholder="Label"
                value={item.label}
                onChange={(e) => setItem(idx, { label: e.target.value })}
              />
              <input
                aria-label={`Checklist item ${idx + 1} help text`}
                className={inputClass}
                placeholder="Help text (optional)"
                value={item.helpText}
                onChange={(e) => setItem(idx, { helpText: e.target.value })}
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-space-2 text-caption">
                  <input
                    type="checkbox"
                    checked={item.required}
                    onChange={(e) => setItem(idx, { required: e.target.checked })}
                  />
                  Required
                </label>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setItems((all) => all.filter((_, i) => i !== idx))}
                  aria-label={`Remove checklist item ${idx + 1}`}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-caption text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-space-2">
        <Button variant="secondary" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void save()}
          loading={pending}
          disabled={name.trim() === "" || ownerRoleCode.trim() === ""}
          data-testid="qa-catalog-save"
        >
          {state.mode === "create" ? "Create area" : "Save area"}
        </Button>
      </div>
    </div>
  );
}
